import { useCallback, useEffect, useRef, useState } from 'react';
import {
	fetchSingleTranslation,
	fetchTranslationGroups,
	fetchTranslations,
	fetchTranslationsBundle,
	fetchTranslationsExport,
	fetchTranslationsImportTemplate,
	postTranslationReset,
	postTranslationsImport,
	postTranslationsResetAll,
	putTranslation
} from './actions';
import {
	ITranslationsCsvFile,
	ITranslationsImportResult,
	ITranslationsQuery,
	ITranslation,
	UseTranslationsParams,
	UseTranslationsReturn
} from './types';

// ==============================|| TRANSLATIONS - HOOK ||============================== //
// Hook-ul unic pentru texte/traduceri (înlocuiește vechiul apel inițial pe api/settings):
//   - lista paginată (GET api/translations) — pagina „Gestionare texte";
//   - bundle-ul plat (GET api/translations/bundle) — aplicat peste i18n la pornire
//     (TranslationsProvider);
//   - grupurile distincte (GET api/translations/groups) — filtrul „Grup";
//   - citire/salvare per text (GET/PUT api/translations/:id) — editările din pagină;
//   - reset la seed: per text (POST api/translations/:id/reset) sau toate cele
//     modificate (POST api/translations/reset);
//   - export CSV (GET api/translations/export) — cu/fără scop („Toate scopurile");
//   - modelul CSV de import (GET api/translations/import-template) — scopul curent;
//   - importul CSV (POST api/translations/import) — all-or-nothing, cu erori pe linie.
// Fiecare zonă se activează prin flag (getTranslationsOnRender / withBundle / withGroups)
// și rămâne apelabilă manual; `enabled` amână totul până în context autentificat.

export function useTranslations({
	platform = 'admin',
	language = 'ro',
	group,
	search,
	onlyModified,
	page = 1,
	items = 50,
	orderBy,
	ascending,
	getTranslationsOnRender = false,
	withGroups = false,
	withBundle = false,
	enabled = true
}: UseTranslationsParams = {}): UseTranslationsReturn {
	const [translations, setTranslations] = useState<ITranslation[]>([]);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(
		Boolean(enabled && getTranslationsOnRender)
	);
	const [error, setError] = useState<string | null>(null);

	const [bundle, setBundle] = useState<Record<string, string>>({});
	const [loadingBundle, setLoadingBundle] = useState<boolean>(
		Boolean(enabled && withBundle)
	);
	const [errorBundle, setErrorBundle] = useState<string | null>(null);

	const [groups, setGroups] = useState<string[]>([]);
	const [loadingGroups, setLoadingGroups] = useState<boolean>(
		Boolean(enabled && withGroups)
	);

	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingReset, setLoadingReset] = useState<boolean>(false);
	const [loadingExport, setLoadingExport] = useState<boolean>(false);
	const [loadingTemplate, setLoadingTemplate] = useState<boolean>(false);
	const [loadingImport, setLoadingImport] = useState<boolean>(false);

	// Gardă anti-race pe listă: la schimbări rapide de pagină/filtre pot fi în zbor mai
	// multe cereri, iar răspunsurile pot sosi în altă ordine — se aplică DOAR ultima.
	const listRequestSeq = useRef(0);

	const getTranslations = useCallback(
		async (overrides: ITranslationsQuery = {}): Promise<void> => {
			listRequestSeq.current += 1;
			const seq = listRequestSeq.current;
			const isCurrent = (): boolean => listRequestSeq.current === seq;
			try {
				setLoading(true);
				setError(null);
				const response = await fetchTranslations(
					{
						platform,
						language,
						group,
						search,
						onlyModified,
						page,
						items,
						orderBy,
						ascending,
						...overrides
					},
					(totalPagesParam, totalCountParam) => {
						if (!isCurrent()) return;
						setTotalPages(totalPagesParam);
						setTotalCount(totalCountParam);
					}
				);
				if (!isCurrent()) return;
				setTranslations(response);
			} catch (e) {
				if (!isCurrent()) return;
				setError(e instanceof Error ? e.message : `${e}`);
				setTranslations([]);
				setTotalPages(0);
				setTotalCount(0);
			} finally {
				if (isCurrent()) setLoading(false);
			}
		},
		[platform, language, group, search, onlyModified, page, items, orderBy, ascending]
	);

	const getBundle = useCallback(async (): Promise<void> => {
		try {
			setLoadingBundle(true);
			setErrorBundle(null);
			setBundle(await fetchTranslationsBundle(platform, language));
		} catch (e) {
			// Bundle-ul pică silențios pe {} — textele locale rămân fallback-ul.
			setErrorBundle(e instanceof Error ? e.message : `${e}`);
			setBundle({});
		} finally {
			setLoadingBundle(false);
		}
	}, [platform, language]);

	const getGroups = useCallback(async (): Promise<void> => {
		try {
			setLoadingGroups(true);
			setGroups(await fetchTranslationGroups(platform, language));
		} catch {
			// Fallback silențios: fără grupuri BE, filtrul se poate deriva din listă.
			setGroups([]);
		} finally {
			setLoadingGroups(false);
		}
	}, [platform, language]);

	const getTranslation = useCallback(async (id: number): Promise<ITranslation | null> => {
		try {
			return await fetchSingleTranslation(id);
		} catch {
			return null;
		}
	}, []);

	const updateTranslation = useCallback(
		async (id: number, value: string, onSuccess?: () => void): Promise<void> => {
			try {
				setLoadingUpdate(true);
				await putTranslation(id, value);
				onSuccess?.();
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	// Folosește loadingUpdate ca și salvarea — e tot o mutație pe un singur text.
	const resetTranslation = useCallback(
		async (id: number, onSuccess?: () => void): Promise<void> => {
			try {
				setLoadingUpdate(true);
				await postTranslationReset(id);
				onSuccess?.();
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	// Exportul CSV nu e legat de platforma hook-ului: fără argumente exportă TOATE
	// scopurile (opțiunea „Toate scopurile"), cu platform+language doar scopul dat.
	const exportTranslations = useCallback(
		async (
			exportPlatform?: string,
			exportLanguage?: string
		): Promise<ITranslationsCsvFile | null> => {
			try {
				setLoadingExport(true);
				return await fetchTranslationsExport(exportPlatform, exportLanguage);
			} catch {
				return null;
			} finally {
				setLoadingExport(false);
			}
		},
		[]
	);

	// Modelul de import e legat de scopul curent al hook-ului (platform+language).
	const getImportTemplate =
		useCallback(async (): Promise<ITranslationsCsvFile | null> => {
			try {
				setLoadingTemplate(true);
				return await fetchTranslationsImportTemplate(platform, language);
			} catch {
				return null;
			} finally {
				setLoadingTemplate(false);
			}
		}, [platform, language]);

	// Rezultatul (inclusiv erorile de validare pe linie) e al apelantului — doar erorile
	// de rețea/neașteptate se notifică aici.
	const importTranslations = useCallback(
		async (file: File): Promise<ITranslationsImportResult | null> => {
			try {
				setLoadingImport(true);
				return await postTranslationsImport(file);
			} catch {
				return null;
			} finally {
				setLoadingImport(false);
			}
		},
		[]
	);

	const resetAllModified = useCallback(
		async (onSuccess?: () => void): Promise<void> => {
			try {
				setLoadingReset(true);
				await postTranslationsResetAll(platform, language);
				onSuccess?.();
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingReset(false);
			}
		},
		[platform, language]
	);

	useEffect(() => {
		if (enabled && getTranslationsOnRender) getTranslations();
	}, [enabled, getTranslationsOnRender, getTranslations]);

	useEffect(() => {
		if (enabled && withBundle) getBundle();
	}, [enabled, withBundle, getBundle]);

	useEffect(() => {
		if (enabled && withGroups) getGroups();
	}, [enabled, withGroups, getGroups]);

	return {
		translations,
		totalCount,
		totalPages,
		loading,
		error,
		getTranslations,
		bundle,
		loadingBundle,
		errorBundle,
		getBundle,
		groups,
		loadingGroups,
		getGroups,
		getTranslation,
		updateTranslation,
		loadingUpdate,
		resetTranslation,
		resetAllModified,
		loadingReset,
		exportTranslations,
		loadingExport,
		getImportTemplate,
		loadingTemplate,
		importTranslations,
		loadingImport
	};
}
